// Theme constants - standalone version for access-registration

export const Palette = {
  helloRed: '#F42246',
  red900: '#7E0018',
  red800: '#A30021',
  red700: '#C6002C',
  red600: '#E4003B',
  red500: '#FD344E',
  red400: '#FF5B66',
  red300: '#FF7F82',
  red200: '#FFA4A2',
  red100: '#FFC9C5',
  red50: '#FFEDEB',
  helloTeal: '#04747A',
  teal900: '#004044',
  teal800: '#005559',
  teal700: '#00696F',
  teal650: '#04747a',
  teal600: '#1C7E84',
  teal500: '#3A9398',
  teal400: '#5AA7AC',
  teal300: '#7CBBBF',
  teal200: '#A0CFD2',
  teal100: '#C5E2E4',
  teal50: '#ECF5F6',
  helloYellow: '#FFB71B',
  yellow900: '#553200',
  yellow800: '#6E4200',
  yellow700: '#865400',
  yellow600: '#9F6600',
  yellow500: '#B67B00',
  yellow400: '#CD9000',
  yellow300: '#E0A63D',
  yellow200: '#F0BE70',
  yellow100: '#FBD7A6',
  yellow50: '#FFF2E1',
  helloPurple: '#BA20A4',
  purple900: '#6B095D',
  purple800: '#8B077A',
  purple700: '#AA1695',
  purple600: '#C42EAD',
  purple500: '#DB4BC3',
  purple400: '#EC69D4',
  purple300: '#F989E2',
  purple200: '#FFABED',
  purple100: '#FFCCF5',
  purple50: '#FFEEFC',
  dark100: '#045454',
  dark: '#132535',
  natural900: '#303A46',
  natural800: '#3F4D5C',
  natural700: '#506072',
  natural600: '#637487',
  natural500: '#77889C',
  natural400: '#8D9DAF',
  natural300: '#A5B2C2',
  natural200: '#BDC7D4',
  natural100: '#D7DDE5',
  natural50: '#F2F4F6',
  white: '#FFFFFF',
  orange: '#FC5912',
  orange300: '#FF8B55',
  secondaryYellow: '#F9D006',
  uxGreen: '#4CE063',
  nearWhite: '#F7F7F3',
  darkNearWhite: '#F1F0EA',
  brightTeal: '#07EDE6',
  brightYellow: '#F4F406',
  brightPurple: '#F706E1',
  transparent: '#FFFFFF00',
};

export const Colors = {
  brand: Palette.helloRed,
  brandDark: Palette.red700,
  brandSecondary: Palette.red200,
  brandSecondaryDark: Palette.red300,
  brandTrinary: Palette.red50,
  primary: Palette.helloTeal,
  primaryDark: Palette.teal800,
  primaryLight: Palette.teal500,
  primaryLighter: Palette.teal200,
  primaryPale: Palette.teal100,
  secondaryPurple: Palette.helloPurple,
  foreground: Palette.dark,
  foregroundFaint: Palette.natural800,
  foregroundDisabled: Palette.natural600,
  foregroundDisabledAlt: Palette.natural200,
  placeholder: Palette.natural200,
  placeholderAlt: Palette.natural300,
  hint: Palette.natural700,
  background: Palette.white,
  backgroundDisabled: Palette.darkNearWhite,
  backgroundAlt: Palette.nearWhite,
  backdrop: Palette.dark,
  error: Palette.helloRed,
  errorBackground: Palette.red200,
  errorBackgroundAlt: Palette.purple50,
  warningLight: Palette.yellow50,
  link: Palette.teal650,
  actionTertiaryPressed: Palette.teal900,
  brandTertiaryPressed: Palette.red700,
  transparent: Palette.transparent,
};

export const Spacing = {
  x0_25: 2,
  x0_5: 4,
  x1: 8,
  x1_5: 12,
  x2: 16,
  x3: 24,
  x4: 32,
  x5: 40,
  x6: 48,
  x7: 56,
  x8: 64,
  x9: 72,
};

export const fontFamily = 'Lato';
export const fontFamilyBold = 'Lato';
export const fontWeightRegular = 400;
export const fontWeightBold = 700;

export const fontStyles: { [key: string]: { fontFamily?: string; fontSize?: number; color?: string; fontWeight?: number; lineHeight?: number; height?: string } } = {
  bold: {
    fontFamily: fontFamilyBold,
    fontWeight: fontWeightBold,
  },
  inverse: {
    fontFamily,
    color: Colors.background,
  },
  h1: { fontFamily, fontSize: 52, color: Colors.foreground },
  h2: { fontFamily, fontSize: 40, color: Colors.foreground },
  h3: { fontFamily, fontSize: 32, color: Colors.foreground },
  h4: { fontFamily, fontSize: 28, color: Colors.foreground },
  h5: { fontFamily, fontSize: 22, color: Colors.foreground },
  h6: { fontFamily, fontSize: 20, color: Colors.foreground },
  p1: { fontFamily, fontSize: 18, color: Colors.foreground, lineHeight: 24 },
  p2: { fontFamily, fontSize: 16, color: Colors.foreground, lineHeight: 24 },
  p3: { fontFamily, fontSize: 14, color: Colors.foreground, lineHeight: 24 },
  p4: { fontFamily, fontSize: 14, color: Colors.foreground, lineHeight: 24 },
  p5: { fontFamily, fontSize: 13, color: Colors.foreground, lineHeight: 16 },
  buttonLarge: { fontFamily: fontFamilyBold, fontWeight: fontWeightBold, fontSize: 20, height: '56px' },
  buttonMedium: { fontFamily: fontFamilyBold, fontWeight: fontWeightBold, fontSize: 16, height: '40px' },
  buttonSmall: { fontFamily: fontFamilyBold, fontWeight: fontWeightBold, fontSize: 14, height: '32px' },
  linkLarge: { color: Colors.primary, fontFamily: fontFamilyBold, fontWeight: fontWeightBold, fontSize: 20 },
  linkMedium: { color: Colors.primary, fontFamily: fontFamilyBold, fontWeight: fontWeightBold, fontSize: 18 },
  linkSmall: { color: Colors.link, fontFamily: fontFamilyBold, fontWeight: fontWeightBold, fontSize: 14 },
};

/** Desktop framed registration layout (rail + clipped column). Shared by CSS media queries and the Layout wheel-forward guard. */
export const DESKTOP_FRAME_MIN_WIDTH_PX = 900;

export const Dimensions = {
  c1: 64, c2: 144, c3: 224, c4: 304, c5: 384, c6: 464, c7: 544, c8: 624, c9: 704, c10: 784, c11: 864, c12: 960,
  headerMaxWidth: 1280,
  gridWidth: 1000,
  // Measured against the real rendered Header (was stale at 72; the Boris
  // HelpButton pill added height here without this constant being updated).
  headerHeight: 88,
};

export const Theme = {
  Colors,
  Palette,
  Spacing,
  Typography: { fontStyles, fontFamily, fontFamilyBold, fontWeightRegular, fontWeightBold },
  Dimensions,
};
